import { useNavigate } from 'react-router';
import type { InvitationResponse } from '../api';
import { Banner, Button, Spinner } from '../components/ui';
import { errorMessage } from '../api/problems';
import {
  useAcceptInvitation,
  useDeclineInvitation,
  useMyInvitations,
} from './invitationQueries';

/**
 * The invitee's decision, wherever they happen to be looking.
 *
 * Rendered on the teams page, on the dedicated `/invitations` route the emails link to, and
 * nowhere else it would be in the way — somebody who has just confirmed their address should not
 * have to work out where an invitation lives.
 */
export function InvitationsCard({ heading = true }: { heading?: boolean }) {
  const invitations = useMyInvitations();
  const accept = useAcceptInvitation();
  const decline = useDeclineInvitation();
  const navigate = useNavigate();

  if (invitations.isPending) {
    return <Spinner label="Loading invitations" />;
  }

  const pending = invitations.data ?? [];
  if (pending.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-3 rounded-md border border-accent-subtle bg-surface-raised p-4">
      {heading && (
        <h2 className="text-sm font-semibold text-text">
          {pending.length === 1 ? 'You have an invitation' : `You have ${pending.length} invitations`}
        </h2>
      )}

      {accept.isError && <Banner tone="error">{errorMessage(accept.error)}</Banner>}
      {decline.isError && <Banner tone="error">{errorMessage(decline.error)}</Banner>}

      <ul className="flex flex-col gap-2">
        {pending.map((invitation) => (
          <li
            key={invitation.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md
              border border-border-subtle px-3 py-2"
          >
            <span className="min-w-0">
              <span className="block truncate text-sm text-text">{describe(invitation)}</span>
              <span className="block truncate text-xs text-text-muted">
                Invited by {invitation.invitedBy || 'someone'} · expires{' '}
                {new Date(invitation.expiresAt).toLocaleDateString()}
              </span>
            </span>

            <span className="flex shrink-0 items-center gap-2">
              <Button
                variant="primary"
                disabled={accept.isPending || decline.isPending}
                onClick={() =>
                  accept.mutate(invitation.id, {
                    onSuccess: (result) => {
                      // Straight to the thing they just gained, rather than leaving them to find it.
                      if (result.kind === 'Team') {
                        void navigate('/teams');
                      }
                    },
                  })
                }
              >
                Accept
              </Button>

              <Button
                variant="ghost"
                disabled={accept.isPending || decline.isPending}
                onClick={() => decline.mutate(invitation.id)}
              >
                Decline
              </Button>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function describe(invitation: InvitationResponse) {
  const name = invitation.targetName || 'something';

  return invitation.kind === 'Team'
    ? `Join ${name} as ${(invitation.role ?? 'Member').toLowerCase()}`
    : `${name} — shared ${invitation.permission === 'Write' ? 'to edit' : 'to read'}`;
}
